import PropTypes from 'prop-types'
import React, { Component } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import videoServices from 'services/video'
import { changeGlobalRecorder } from 'actions'
import {
  setBgOptions,
  setOpenQuestion,
  toggleRecordingState,
  videoUploadFinished,
  videoUploadInProgress,
} from 'actions/video-response'
import { mapLanguageToComponentProps } from 'i18n'
import RecorderButtons from 'components/recorder-buttons'
import config from 'config'
import {
  IS_WEBM_SUPPORTED_SAFARI,
  IS_MP4_SUPPORTED_SAFARI,
  USE_HIGH_QUALITY_VIDEO_RECORDING,
  IS_VIRTUAL_BG_SUPPORTED,
  IS_VIRTUAL_BG_BLUR_SUPPORTED,
} from 'features'
import * as analytics from 'analytics/performance'
import { Button } from 'components/styled/button'
import { Body, PrimaryLink, Subtitle } from 'components/styled/typography'
import Confirm from 'components/confirm/confirm'
import {
  AbsoluteFillStyle,
  AbsoluteStyle,
  CenterizedContentStyle,
  RelativeContainerStyle,
} from 'theme'
import { imagePath } from 'utils/image'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation'
import {
  VIRTUAL_BG_BLUR_AMOUNT,
  HIGH_QUALITY_VIDEO_DIMENSIONS,
  VIDEO_MIMETYPES,
  VIDEO_DIMENSIONS,
  SAFARI_MP4_RECORDERS,
  BROWSER_RECORDERS,
} from 'app-constants'
import { Flex } from 'components/styled/containers/flex'
import {
  BACKGROUND_THUMBS,
  BG_IMAGE_NAMES,
  getImageSrc,
  isInBgImages,
} from './bg-images-handlers'
import VirtualBg from './virtual-bg'

const IS_NEWER_SAFARI = IS_MP4_SUPPORTED_SAFARI || IS_WEBM_SUPPORTED_SAFARI

const propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  video: PropTypes.shape({
    videoUrl: PropTypes.string,
    recorder: PropTypes.string,
    playbackVideoUrl: PropTypes.string,
    has: PropTypes.bool,
    background: PropTypes.string,
    blurAmount: PropTypes.number,
  }).isRequired,
  mode: PropTypes.oneOf(['admin', 'interview']).isRequired,
  onClose: PropTypes.func,
  onError: PropTypes.func,
  onRemove: PropTypes.func,
  onVideoSubmit: PropTypes.func,
  updateVideo: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  responseVideoUrl: PropTypes.string,
  disableRecording: PropTypes.bool,
  openQuestion: PropTypes.string,
  bgOptions: PropTypes.shape({
    background: PropTypes.string,
    blurAmount: PropTypes.number,
  }),
  videoEventProps: PropTypes.shape({}),
  locationId: PropTypes.string,
  isEmbedded: PropTypes.bool,
  companyVideoBgImages: PropTypes.arrayOf(PropTypes.object),
}

const defaultProps = {
  className: '',
  width: VIDEO_DIMENSIONS.width, // 640
  height: VIDEO_DIMENSIONS.height, // 480
  onClose: () => {},
  onError: () => {},
  onRemove: () => {},
  onVideoSubmit: undefined,
  updateVideo: false,
  responseVideoUrl: '',
  disableRecording: false,
  openQuestion: null,
  bgOptions: undefined,
  videoEventProps: undefined,
  locationId: '',
  isEmbedded: false,
  companyVideoBgImages: [],
}

const loading = keyframes`
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
`

const RecordingContainer = styled.div`
  max-width: 640px;
  margin: 0 auto;
`

const StyledWebcam = styled.div`
  position: relative;
  padding-top: 75%;
  height: 100%;
  user-select: none;

  border: ${({ state, theme }) =>
    state === 'error' && `1px solid ${theme.color.grey07}`};
`

export const WebcamTime = styled(Body)`
  color: ${({ theme }) => theme.color.white};
  position: absolute;
  background-color: ${({ $textQuestion, theme }) =>
    $textQuestion ? 'transparent' : theme.color.blackO50};
  padding: ${({ theme }) => theme.space.s4};
  right: ${({ theme }) => theme.space.s12};
  bottom: ${({ theme }) => theme.space.s12};
  z-index: 1000;
`

const StyledBgCanvas = styled.canvas`
  position: absolute;
  left: ${({ blur }) =>
    blur === VIRTUAL_BG_BLUR_AMOUNT.large ? '-1.5%' : '-0.5%'};
  top: ${({ blur }) =>
    blur === VIRTUAL_BG_BLUR_AMOUNT.large ? '-1.5%' : '-0.5%'};
  width: ${({ blur }) =>
    blur === VIRTUAL_BG_BLUR_AMOUNT.large ? '103%' : '101%'};
  height: ${({ blur }) =>
    blur === VIRTUAL_BG_BLUR_AMOUNT.large ? '103%' : '101%'};
  z-index: 1;
  display: ${({ state }) =>
    state === 'pause' || state === 'play' ? 'none' : 'block'};
`

const OverlayActionsContainer = styled.div`
  z-index: 2;
`

const WebcamContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

export const ConvertingStyle = css`
  animation: ${loading} 2s ease infinite;
  background-image: ${({ theme }) =>
    `linear-gradient(270deg, ${theme.color.primary110}, ${theme.color.grey07}) !important`};
  background-size: 400%;
`

export const ProgressText = styled(Body)`
  color: ${({ theme, $progress }) =>
    $progress < 30 ? theme.color.bodyText : theme.color.white};
`

export const WebcamProgress = styled.div`
  bottom: 0;
  height: auto;
  position: absolute;
  text-align: center;
  width: 100%;
  z-index: 1000;
  padding: ${({ theme }) => theme.space.s8};
  background-image: ${({ theme, $progress }) =>
    `linear-gradient(to right, ${theme.color.primary110} 0%, ${theme.color.primary110} ${$progress}%, ${theme.color.grey07} ${$progress}%, ${theme.color.grey07} 100%)`};
  ${({ $converting }) => $converting && ConvertingStyle};
`

export const StyledCountdown = styled(Subtitle)`
  z-index: 2;
  color: ${({ theme }) => theme.color.white};
  font-size: ${({ theme }) => theme.space.s128};
  line-height: ${({ theme }) => theme.space.s128};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

export const WebcamViewContainer = styled.div`
  ${RelativeContainerStyle};
  ${AbsoluteStyle};
  overflow: hidden;
  border-radius: ${({ theme }) => theme.space.s12};
  text-align: center;
`

export const StyledVideo = styled.video`
  ${AbsoluteFillStyle};
  background-color: ${({ theme }) => theme.color.black};
  z-index: 500;

  display: ${({ state, hideInPreview }) =>
    (state === 'preview' && !hideInPreview) ||
    state === 'countdown' ||
    state === 'record' ||
    state === 'pause' ||
    state === 'play'
      ? 'block'
      : 'none'};

  transform: ${({ state }) =>
    (state === 'preview' || state === 'countdown' || state === 'record') &&
    'scalex(-1)'};
`

export const CenterizedContainer = styled.div`
  ${AbsoluteFillStyle};
  ${CenterizedContentStyle};
  flex-direction: column;
  z-index: ${({ state }) => (state === 'play' ? 499 : 1000)};
  border-radius: ${({ theme }) => theme.space.s12};
`

export const CloseButtonContainer = styled.div`
  display: ${props => (props.$state === 'preview' ? 'inline-block' : 'none')};
  position: absolute;
  top: ${({ theme }) => theme.space.s12};
  right: ${({ theme }) => theme.space.s12};
`
class WebcamHtml extends Component {
  constructor(props) {
    super(props)

    this.streamsStarted = false

    this.video = React.createRef()
    this.snapshotCanvas = React.createRef()
    this.bgCanvas = React.createRef()
    this.canvasWrapper = React.createRef()
    this.bgContextRef = React.createRef()

    this.bg = new Image()
    this.bg.crossOrigin = 'anonymous'
    this.drawHeight = 540
    this.drawWidth = 720
    this.offsetX = 0
    this.offsetY = 0
    this.mimeType = IS_NEWER_SAFARI // switch back to recording mp4 in safari as there was issue for some suers with webm
      ? VIDEO_MIMETYPES.mp4
      : VIDEO_MIMETYPES.webm
    this.audioTrack = undefined
    this.videoChunks = []
    this.mediaRecorder = undefined
    this.canvasStream = undefined
    this.animationFrame = undefined
    this.bg.src = this.getCompanyVideoBgSrc() || getImageSrc()
    this.videoRecorder = undefined
    this.videoWidth = props.width // 640
    this.videoHeight = props.height // 480
    this.selfieSegmentation = undefined
    this.initialSelectedBg = this.initialSelectBg()
    this.state = {
      useVirtualBg: false,
      state: null,
      memoryError: false,
      muted: true,
      videoSource: null,
      count: null,
      time: null,
      progress: null,
      progressState: null,
      confirm: null,
      selectedBg: this.initialSelectedBg.background,
      blur: VIRTUAL_BG_BLUR_AMOUNT.small,
      endBgImageIndex: 5,
      selfieSegmentationReady: false,
    }

    this.blobTypes = ['video/webm', 'video/mp4']
    this.sendToMediaPipe = this.sendToMediaPipe.bind(this)
    this.handleMediaRecorderStop = this.handleMediaRecorderStop.bind(this)
    this.handleDataAvailable = this.handleDataAvailable.bind(this)
    this.onSelfieSegmentationResults =
      this.onSelfieSegmentationResults.bind(this)

    this.onBeforeUnload = event => {
      const { i18n } = this.props
      if (this.uploadPromise && this.uploadPromise.pending()) {
        this.notifyWhenUploaded = true
        return (event.returnValue = i18n.webcam.uploadInProgress)
      }
      this.clearVirtualBg()
      this.stopStreams()
    }
  }

  componentDidMount() {
    const { video, dispatch } = this.props
    this.initSelfieSegmentation()
    // does the same with all the response object on the page, so the last response's info will be the one set in the end
    if (
      video &&
      video.background &&
      (video.background === 'blur' ||
        this.isInCompanyBgImages(video.background) ||
        isInBgImages(video.background))
    ) {
      dispatch(
        setBgOptions({
          background: video.background,
          blurAmount: video.blurAmount || 0,
        })
      )
    }
    this.setUpStreams()
  }

  componentDidUpdate(prevProps, prevState) {
    const { openQuestion, video } = this.props
    const { selfieSegmentationReady, state, progress } = this.state

    if (this.video && this.video.current) {
      this.video.current.onpause = () => {
        this.setState({ showRetakeAndRemove: true })
      }

      this.video.current.onplay = () => {
        this.setState({ showRetakeAndRemove: false })
      }
    }

    // if response stream for other question is opened,
    // we want to close the stream for previous question so virtual bg works

    if (
      selfieSegmentationReady !== prevState.selfieSegmentationReady &&
      selfieSegmentationReady &&
      this.selfieSegmentation
    ) {
      this.selfieSegmentation.setOptions({
        modelSelection: 1,
        selfieMode: true,
      })
      this.selfieSegmentation.onResults(this.onSelfieSegmentationResults)
    }

    if (
      openQuestion &&
      prevProps.openQuestion &&
      prevProps.openQuestion !== openQuestion &&
      openQuestion !== video.questionId
    ) {
      if (video && !video.has && !progress && state !== 'pause') {
        // close open stream if in preview/recording mode if the questionId does not match open question
        this.close()
      } else if (state !== 'pause') {
        this.stop()
      }
      this.stopStreams()
      this.clearVirtualBg()
    }
  }

  componentWillUnmount() {
    removeEventListener('beforeunload', this.onBeforeUnload)
    this.clearVirtualBg()
    this.stopStreams()
    this.stopTimer()
  }

  setUpStreams() {
    addEventListener('beforeunload', this.onBeforeUnload)
    this.bgContextRef.current = this.bgCanvas.current.getContext('2d')

    this.setState({ state: 'start' }, () => {
      const { video } = this.props
      const shouldUsePlaybackUrl =
        (video && video.recorder === 'opentok') ||
        (video && BROWSER_RECORDERS.includes(video.recorder) && IS_NEWER_SAFARI)
      if (video && video.has) {
        if (video.recorder && shouldUsePlaybackUrl) {
          this.setState({
            state: 'pause',
            muted: false,
            videoSource: video.playbackVideoUrl,
          })
        } else {
          this.downloadBlobs(this.blobTypes)
        }
      } else {
        this.startStreams().then(() => this.viewStreams())
      }
    })
  }

  setSelectedBg(selected, blurAmount = 5, companyVideoBgImages) {
    const { dispatch } = this.props
    if (selected !== 'blur') {
      const companyBg =
        companyVideoBgImages && companyVideoBgImages.length > 0
          ? companyVideoBgImages.find(bg => bg.file === selected)
          : undefined
      this.bg.onload = () => {
        const imgWidth = companyBg ? companyBg.width : 720
        const imgHeight = companyBg ? companyBg.height : 540
        const imgAspectRatio = imgWidth / imgHeight
        const canvas = this.bgCanvas.current
        const canvasAspectRatio = canvas.width / canvas.height
        if (imgAspectRatio > canvasAspectRatio) {
          // Image is wider than canvas
          this.drawHeight = canvas.height
          this.drawWidth = imgAspectRatio * this.drawHeight
          this.offsetX = (canvas.width - this.drawWidth) / 2
          this.offsetY = 0
        } else {
          // Image is taller than canvas
          this.drawWidth = canvas.width
          this.drawHeight = this.drawWidth / imgAspectRatio
          this.offsetX = 0
          this.offsetY = (canvas.height - this.drawHeight) / 2
        }
      }
      if (companyBg && imagePath(companyBg) && selected === companyBg.file) {
        this.bg.src = imagePath(companyBg)
      } else {
        this.bg.src = getImageSrc(selected)
      }
      this.setState({ selectedBg: selected, blur: blurAmount })
    }

    const bgOptions = { background: selected, blurAmount }
    this.setState({ selectedBg: selected, blur: blurAmount })
    this.updateVideo(bgOptions)
    dispatch(setBgOptions(bgOptions))
  }

  getRecorder() {
    const { video } = this.props
    const { useVirtualBg } = this.state
    const videoHasBg = video.background && useVirtualBg
    if (IS_NEWER_SAFARI) {
      // safari does not currently support the video bg but just in case it once does
      // const mimeTypePart = IS_WEBM_SUPPORTED_SAFARI ? 'webm' : 'mp4'
      // revert to mp4 usage only for safari
      return videoHasBg
        ? `safari-mp4-mediarecorder-bg`
        : `safari-mp4-mediarecorder-normal`
    }
    if (videoHasBg) {
      return 'mediarecorder-bg'
    }
    // used to be 'rtc'; the mediarecorder is already used in some checks, so not changing the one used for bg recorder to something else
    return 'mediarecorder-normal'
  }

  startStreams() {
    const { i18n, width, height } = this.props
    const { useVirtualBg } = this.state

    if (this.streamsStarted) {
      return Promise.resolve()
    }

    this.videoWidth =
      USE_HIGH_QUALITY_VIDEO_RECORDING || useVirtualBg
        ? HIGH_QUALITY_VIDEO_DIMENSIONS.width
        : width
    this.videoHeight =
      USE_HIGH_QUALITY_VIDEO_RECORDING || useVirtualBg
        ? HIGH_QUALITY_VIDEO_DIMENSIONS.height
        : height

    const video = {
      width: { min: width, ideal: this.videoWidth },
      height: { min: height, ideal: this.videoHeight },
      frameRate: 30,
      sampleRate: 48000,
      sampleSize: 16,
      channelCount: 1,
      autoGainControl: 1,
    }

    const isMobile = navigator.userAgentData
      ? navigator.userAgentData.mobile
      : false
    // add facingMode property if windows desktop is not in use
    if (!(/windows/i.test(navigator.userAgent) && !isMobile)) {
      video.facingMode = 'user'
    }

    return navigator.mediaDevices
      .getUserMedia({ audio: true, video })
      .then(videoStream => {
        const audioTracks = videoStream.getAudioTracks()
        this.audioTrack = audioTracks[0]
        this.videoStream = videoStream
        this.streamsStarted = true
        if (IS_VIRTUAL_BG_SUPPORTED) {
          this.canvasStream = this.bgCanvas.current.captureStream(30)
          this.canvasStream.addTrack(this.audioTrack)
        }
      })
      .catch(error => {
        // Check if we have user friendly error message or fallback to generic
        // "restart browser" error
        const errorMessage =
          'name' in error && error.name in i18n.webcam.error
            ? i18n.webcam.error[error.name]
            : i18n.webcam.error.stream
        this.setState({ state: 'error', errorMessage, useVirtualBg: false })
        throw error
      })
  }

  stopStreams() {
    if (this.streamsStarted) {
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => {
          track.stop()
        })
      }
      if (this.canvasStream) {
        this.canvasStream.getTracks().forEach(track => {
          track.stop()
        })
      }
      this.streamsStarted = false
    }
  }

  viewStreams() {
    // Check if browser supports srcObject, if not fallback to ObjectUrl
    try {
      this.video.current.srcObject = this.videoStream
      this.setState(
        {
          state: 'preview',
          muted: true,
        },
        () => {
          this.video.current.play()
        }
      )
    } catch (error) {
      this.setState(
        {
          state: 'preview',
          muted: true,
          videoSource: URL.createObjectURL(this.videoStream),
        },
        () => {
          this.video.current.play()
        }
      )
    }
  }

  viewBlobs() {
    const { video } = this.props
    const shouldUsePlaybackUrl =
      (video && video.recorder === 'opentok') ||
      (video && BROWSER_RECORDERS.includes(video.recorder) && IS_NEWER_SAFARI)

    const videoSource = shouldUsePlaybackUrl
      ? video.playbackVideoUrl
      : URL.createObjectURL(this.videoBlob)
    this.setState({
      state: 'pause',
      muted: false,
      videoSource,
    })
  }

  startCountdown() {
    let count = 3
    this.setState({ state: 'countdown', count }, () => {
      this.countdown = setInterval(() => {
        count -= 1
        if (!count) {
          count = null
          clearInterval(this.countdown)
          delete this.countdown
        }
        this.setState({ count }, () => {
          if (!count) {
            this.startRecording()
          }
        })
      }, 1000)
    })
  }

  stopCountdown() {
    clearInterval(this.countdown)
    delete this.countdown
    const { video } = this.props
    this.setState({ count: null }, () => {
      if (video.has) {
        this.video.current.srcObject = undefined
        this.viewBlobs()
      } else {
        this.viewStreams()
      }
    })
  }

  handleBlob(data) {
    const { i18n } = this.props
    if (this.videoBlob.size < 1) {
      // TODO: any other data that can be added here? webcam recording can be either userside or candidate side
      // and not necessarily in any position (library)
      analytics.onErrorEvent({
        locationId: 'webcam',
        id: 'stopRecording',
        properties: {
          action: 'Empty Blob created by recorder',
        },
      })
      this.setState({
        state: 'error',
        memoryError: true,
        useVirtualBg: false,
        errorMessage: i18n.webcam.error.emptyBlob,
      })
      return
    }

    // remove srcObject if it exists, add recorded blob to src
    if (this.video && this.video.current) {
      this.video.current.srcObject = undefined
      this.video.current.src = URL.createObjectURL(this.videoBlob)
    }

    this.setState(
      {
        state: 'pause',
        muted: false,
        videoSource: data,
        time: null,
      },
      () => this.uploadBlobs([this.videoBlob])
    )
  }

  handleDataAvailable(event) {
    if (event.data && event.data.size > 0) {
      this.videoChunks.push(event.data)
    }
  }

  handleMediaRecorderStop() {
    this.mediaRecorder = undefined
    this.videoBlob = new Blob(this.videoChunks, { type: this.mimeType })
    const blobUrl = window.URL.createObjectURL(this.videoBlob)
    this.videoChunks = []
    this.handleBlob(blobUrl)
  }

  initRecorder(stream) {
    this.mediaRecorder = new MediaRecorder(stream, {
      mimeType: this.mimeType,
      videoBitsPerSecond: 800000,
      audioBitsPerSecond: 80000,
      getNativeBlob: false,
    })

    this.mediaRecorder.onstop = this.handleMediaRecorderStop
    this.mediaRecorder.ondataavailable = this.handleDataAvailable
    this.mediaRecorder.start(1000) // timeSlice 1s
  }

  startRecording() {
    const { useVirtualBg } = this.state
    const useCanvasStream =
      useVirtualBg && IS_VIRTUAL_BG_SUPPORTED && this.canvasStream
    this.abortUpload()
    this.removeRecording()

    const stream = useCanvasStream ? this.canvasStream : this.videoStream
    this.setState({ state: 'record' }, () => {
      this.initRecorder(stream)
      this.recordingStarted()
    })
  }

  stopRecording() {
    const { i18n, dispatch } = this.props
    this.stopTimer()
    this.setState({ time: i18n.webcam.processing })

    if (this.mediaRecorder) {
      this.mediaRecorder.stop()
    }
    dispatch(toggleRecordingState(false))
  }

  recordingStarted() {
    const { dispatch } = this.props

    this.startTimer()
    this.uploadScreenshot()
    dispatch(toggleRecordingState(true))
  }

  removeRecording() {
    const { responseVideoUrl, video } = this.props
    const videoUrl =
      video.recorder === 'opentok' && responseVideoUrl
        ? responseVideoUrl
        : video.videoUrl
    delete this.videoBlob
    this.updateVideo({ has: false, recorder: null, videoUrl })
  }

  startPlaying() {
    this.setState({ state: 'play' }, () => {
      this.video.current.play()
      this.startTimer()
    })
  }

  stopPlaying() {
    this.setState({ state: 'pause' }, () => {
      this.stopTimer()
      this.video.current.pause()
      this.video.current.currentTime = 0
    })
  }

  startTimer() {
    let time = 0
    this.setState({ time: '0:00' }, () => {
      // Somehow this get called twice sometimes and with this return we
      // prevent multiple intervals to run same time
      if (this.timer) {
        return
      }

      this.timer = setInterval(() => {
        time += 1
        const seconds = time % 60
        const minutes = (time - seconds) / 60
        this.setState({
          time: `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`,
        })
      }, 1000)
    })
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer)
      delete this.timer
    }
    this.setState({ time: null })
  }

  abortUpload() {
    // If upload is in progress cancel it.
    if (typeof this.abortToken === 'object' && 'abort' in this.abortToken) {
      this.abortToken.abort()
    }
    delete this.notifyWhenUploaded
    this.setState({ progress: null })
  }

  onEnded() {
    const { state } = this.state
    if (state === 'play') {
      this.stopTimer()
      this.viewBlobs()
    }
  }

  onTransitionEnd() {
    const { progress } = this.state
    if (progress === 100) {
      this.setState({ progress: null })
    }
  }

  record() {
    const { video, openQuestion, dispatch, videoEventProps, locationId } =
      this.props
    const { state } = this.state
    if (state === 'play') {
      Promise.resolve(this.stopPlaying()).then(() => {
        this.startStreams().then(() => {
          this.viewStreams()
          this.startCountdown()
        })
      })
    } else {
      if (video.questionId !== openQuestion && openQuestion !== null) {
        // change open question on record if question is changed
        dispatch(setOpenQuestion(video.questionId))
      }

      if (state === 'preview') {
        this.startCountdown()
      } else if (state === 'pause') {
        // update so that before retake sets up the virtual bg if video has them set???
        this.startStreams().then(() => {
          this.viewStreams()
          this.startCountdown()
        })
      }
    }

    analytics.onClickEvent({
      locationId,
      id: 'recordVideoButton',
      properties: {
        ...videoEventProps,
        recorder: video.recorder,
        hasBackground: !!video.background,
      },
    })
  }

  play() {
    const { videoEventProps, locationId, video } = this.props
    const { state } = this.state
    if (state === 'pause') {
      this.startPlaying()

      analytics.onClickEvent({
        locationId,
        id: 'recordedVideoPlayButton',
        properties: {
          ...videoEventProps,
          recorder: video.recorder,
          hasBackground: !!video.background,
        },
      })
    }
  }

  stop(containerClick = false) {
    const { state } = this.state
    if (state === 'countdown') {
      this.stopCountdown()
    } else if (state === 'record') {
      this.stopRecording()
    } else if (!containerClick && state === 'play') {
      this.stopPlaying()
    }
  }

  togglePlay() {
    const { state } = this.state
    state === 'pause' ? this.play() : this.stop(true)
  }

  remove() {
    const { mode, video, onRemove } = this.props
    this.setState({ state: 'loading' })
    if (
      (video && video.recorder === 'opentok') ||
      (video && BROWSER_RECORDERS.includes(video.recorder) && IS_NEWER_SAFARI)
    ) {
      this.removeRecording()
      onRemove()
    } else {
      this.abortUpload()
      this.deleteBlobs()
        .then(() => {
          this.removeRecording()
          return this.startStreams()
        })
        .then(() => {
          if (mode === 'admin') {
            this.viewStreams()
          }
          // Not the prettiest solution, but without timeout Webcam might be
          // removed before viewStreams has updated component's state and this
          // startStreams -> viewStreams thing is needed on admin side where
          // remove take will keep camera open
          setTimeout(() => {
            onRemove()
          }, 0)
        })
    }
  }

  close() {
    const { onClose } = this.props
    const { useVirtualBg } = this.state
    if (useVirtualBg) {
      Promise.resolve(this.clearVirtualBg()).then(() => {
        this.setState({ state: 'close' }, () => {
          onClose()
        })
      })
    } else {
      this.setState({ state: 'close' }, () => {
        onClose()
      })
    }
  }

  clearVirtualBg() {
    const { video } = this.props
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)

      if (this.bgContextRef && this.bgContextRef.current) {
        this.bgContextRef.current.clearRect(
          0,
          0,
          HIGH_QUALITY_VIDEO_DIMENSIONS.width,
          HIGH_QUALITY_VIDEO_DIMENSIONS.height
        )
      }
    }
    this.setState({
      selectedBg: this.initialSelectedBg.background,
      blur: this.initialSelectedBg.blurAmount,
      useVirtualBg: false,
    })

    if (!video.has) {
      this.updateVideo({ background: undefined, blurAmount: undefined })
    }
  }

  onToggleBg() {
    const { dispatch, bgOptions } = this.props
    const { useVirtualBg } = this.state
    const blurSupported =
      config.SHOW_BLUR_FOR_VIRTUAL_BG && IS_VIRTUAL_BG_BLUR_SUPPORTED
    const useBgOptions =
      bgOptions &&
      bgOptions.background &&
      ((bgOptions.background === 'blur' && blurSupported) ||
        this.isInCompanyBgImages(bgOptions.background) ||
        isInBgImages(bgOptions.background))

    if (!useVirtualBg) {
      this.setState({ useVirtualBg: true, muted: true }, () => {
        Promise.resolve(this.stopStreams())
          .then(() => {
            this.setUpStreams()
          })
          .then(() => {
            const initBgOptions = useBgOptions
              ? bgOptions
              : this.initialSelectedBg

            dispatch(setBgOptions(initBgOptions))
            this.sendToMediaPipe()
          })
      })
    } else {
      this.setState({ useVirtualBg: false, muted: false }, () => {
        Promise.resolve(this.stopStreams()).then(() => {
          this.setUpStreams()
          this.clearVirtualBg()
        })
      })
    }
  }

  uploadScreenshot() {
    const { width, height } = this.props
    const { useVirtualBg } = this.state
    const canvas = useVirtualBg ? this.bgCanvas : this.snapshotCanvas
    const snapshotContext = canvas.current.getContext('2d')

    if (!useVirtualBg) {
      snapshotContext.drawImage(this.video.current, 0, 0, width, height)
    }

    const { mode, video } = this.props
    const snapshot = canvas.current.toDataURL('image/jpeg', 1)
    const binary = atob(snapshot.split(',')[1])
    const data = []

    for (let i = 0; i < binary.length; i += 1) {
      data.push(binary.charCodeAt(i))
    }

    const blob = new Blob([new Uint8Array(data)])

    videoServices.uploadScreenshot(`/${mode}`, video.videoUrl, blob)
  }

  sendToMediaPipe() {
    const { useVirtualBg } = this.state
    if (!useVirtualBg) {
      this.clearVirtualBg()
    } else if (
      !this.streamsStarted ||
      (this.video.current &&
        (!this.video.current.videoWidth ||
          this.video.current.videoWidth !==
            HIGH_QUALITY_VIDEO_DIMENSIONS.width))
      // 720 is the width for the video bg video stream
      // we only want to send to mediapipe when the virtual bg video stream is ready
    ) {
      window.requestAnimationFrame(this.sendToMediaPipe)
    } else if (this.selfieSegmentation) {
      this.selfieSegmentation.send({ image: this.video.current }).then(() => {
        this.animationFrame = window.requestAnimationFrame(this.sendToMediaPipe)
      })
    }
  }

  onSelfieSegmentationResults(results) {
    const { selectedBg, blur } = this.state
    const context = this.bgContextRef && this.bgContextRef.current
    const canvas = this.bgCanvas && this.bgCanvas.current
    const mask = results.segmentationMask

    try {
      if (context && canvas) {
        context.save()
        context.clearRect(0, 0, canvas.width, canvas.height)
        context.drawImage(mask, 0, 0, canvas.width, canvas.height)
        context.fillStyle = 'transparent'

        // SET ORIGINAL IMAGE WHEN SOUCE-IN (prevents red mask flashing when changing bg)
        context.globalCompositeOperation = 'source-in'
        context.drawImage(results.image, 0, 0, canvas.width, canvas.height)

        // BACKGROUND
        context.globalCompositeOperation = 'source-out'
        if (selectedBg === 'blur') {
          context.filter = `blur(${blur}px)`
          context.drawImage(results.image, 0, 0, canvas.width, canvas.height)
        } else {
          context.filter = 'blur(0px)'
          context.drawImage(
            this.bg,
            this.offsetX,
            this.offsetY,
            this.drawWidth,
            this.drawHeight
          )
        }

        // FOREGROUND
        context.globalCompositeOperation = 'destination-atop'
        context.filter = 'blur(0px)'
        context.drawImage(results.image, 0, 0, canvas.width, canvas.height)

        context.restore()
      }
    } catch (error) {
      const nextSelectedIndex =
        selectedBg === BG_IMAGE_NAMES[BG_IMAGE_NAMES.indexOf(selectedBg)]
          ? BG_IMAGE_NAMES.indexOf(selectedBg) + 1
          : 0

      this.setSelectedBg(
        BG_IMAGE_NAMES[nextSelectedIndex] || 'blur',
        VIRTUAL_BG_BLUR_AMOUNT.small
      )
      console.error(error)
      if (typeof Raven !== 'undefined') {
        const extra = {}
        if ('response' in error) {
          extra.response = error.response
        }
        Raven.captureException(error, {
          extra,
        })
      }
    }
  }

  downloadBlobs(mimetypes) {
    const { i18n, mode, video } = this.props
    this.setState({ progress: 0 }, () => {
      videoServices
        .downloadBlobs(
          `/${mode}`,
          video.videoUrl,
          video.recorder,
          mimetypes,
          ({ progress }) => {
            this.setState({
              progress: (progress * 100).toFixed(1),
              progressState: i18n.webcam.downloading,
            })
          }
        )
        .then(blobs => {
          blobs.forEach(blob => {
            this.videoBlob = blob
          })
          this.setState({ progress: null }, () => {
            this.viewBlobs()
          })
        })
        .catch(() => {
          this.setState({
            progress: null,
            useVirtualBg: false,
            confirm: {
              message: i18n.webcam.error.download,
              onCancel: () => {
                this.setState({ confirm: null }, () => {
                  this.updateVideo({ has: false, recorder: null })
                  this.startStreams().then(() => this.viewStreams())
                })
              },
              onConfirm: () => {
                this.setState({ confirm: null }, () =>
                  this.downloadBlobs(mimetypes)
                )
              },
            },
          })
        })
    })
  }

  toggleShowBgImages() {
    const { endBgImageIndex } = this.state

    if (endBgImageIndex === BACKGROUND_THUMBS.length - 1) {
      this.setState({
        endBgImageIndex: 5,
      })
    } else {
      this.setState({
        endBgImageIndex: BACKGROUND_THUMBS.length - 1,
      })
    }
  }

  initSelfieSegmentation() {
    if (IS_VIRTUAL_BG_SUPPORTED) {
      this.selfieSegmentation = new SelfieSegmentation({
        locateFile: file =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
      })
      this.setState({ selfieSegmentationReady: true })
    }
  }

  uploadBlobs(blobs) {
    const { dispatch, mode, video, i18n } = this.props
    dispatch(videoUploadInProgress(video))
    this.setState(
      {
        progress: { left: 0 },
        progressState: i18n.webcam.uploading,
      },
      () => {
        this.abortToken = {}
        videoServices
          .uploadBlobs(
            `/${mode}`,
            video.videoUrl,
            blobs,
            ({ progress }) => {
              const newState = {
                progress: (progress * 100).toFixed(1),
              }

              if (progress === 1) {
                newState.progressState = i18n.webcam.converting
              }

              this.setState(newState)
            },
            this.abortToken
          )
          .then(() => {
            delete this.abortToken
            dispatch(videoUploadFinished(video))
            this.setState({ progress: null }, () => {
              const recorder = this.getRecorder()
              this.updateVideo({ has: true, recorder })
              if (this.notifyWhenUploaded) {
                alert(i18n.webcam.uploaded)
              }
            })
          })
          .catch(error => {
            // If upload was aborted because of retake we don't need to do any error handling
            if (error === 'aborted') {
              return
            }

            dispatch(videoUploadFinished(video))
            this.setState({
              progress: null,
              useVirtualBg: false,
              confirm: {
                message: i18n.webcam.error.upload,
                onCancel: event => {
                  this.remove(event)
                  this.setState({ confirm: null })
                },
                onConfirm: () =>
                  this.setState({ confirm: null }, () =>
                    this.uploadBlobs(blobs)
                  ),
              },
            })

            if (typeof Raven !== 'undefined') {
              const extra = { video }
              if ('response' in error) {
                extra.response = error.response
              }
              Raven.captureException(error, {
                extra,
              })
            }
          })
          .then(() => {
            delete this.notifyWhenUploaded
          })
      }
    )
  }

  deleteBlobs() {
    const { mode, video } = this.props
    const mimeType =
      video && SAFARI_MP4_RECORDERS.includes(video.recorder)
        ? VIDEO_MIMETYPES.mp4
        : VIDEO_MIMETYPES.webm
    return videoServices.deleteBlobs(`/${mode}`, video.videoUrl, mimeType)
  }

  updateVideo(video) {
    const { dispatch, updateVideo } = this.props
    updateVideo ? updateVideo(video) : dispatch(actions.merge('video', video))
  }

  changeRecorder() {
    const { dispatch, onError } = this.props
    this.removeRecording()
    dispatch(changeGlobalRecorder({ recorder: 'upload' }))
    onError()
  }

  selectButtons() {
    const { state } = this.state

    if (['preview', 'pause'].indexOf(state) > -1) {
      return 'record'
    }
    if (['countdown', 'record', 'play'].indexOf(state) > -1) {
      return 'stop'
    }
    return ''
  }

  initialSelectBg() {
    const { companyVideoBgImages } = this.props
    if (companyVideoBgImages && companyVideoBgImages.length > 0) {
      return {
        background: companyVideoBgImages[0].file,
        blurAmount: VIRTUAL_BG_BLUR_AMOUNT.small,
      }
    }
    if (config.SHOW_BLUR_FOR_VIRTUAL_BG && IS_VIRTUAL_BG_BLUR_SUPPORTED) {
      return {
        background: 'blur',
        blurAmount: VIRTUAL_BG_BLUR_AMOUNT.small,
      }
    }
    return {
      background: BG_IMAGE_NAMES[0],
      blurAmount: VIRTUAL_BG_BLUR_AMOUNT.small,
    }
  }

  getCompanyVideoBgSrc() {
    const { companyVideoBgImages } = this.props
    if (
      companyVideoBgImages &&
      companyVideoBgImages.length > 0 &&
      imagePath(companyVideoBgImages[0])
    ) {
      return imagePath(companyVideoBgImages[0])
    }
    return undefined
  }

  isInCompanyBgImages(bg) {
    const { companyVideoBgImages } = this.props
    return companyVideoBgImages.some(companyBg => companyBg.file === bg)
  }

  render() {
    const {
      disableRecording,
      onVideoSubmit,
      width,
      height,
      video,
      i18n,
      bgOptions,
      mode,
      isEmbedded,
      companyVideoBgImages,
    } = this.props
    // children was only used for the submit button so instead of children just render submit button if this is true
    // if children are needed later, we can add those later
    const {
      state,
      memoryError,
      errorMessage,
      videoSource,
      muted,
      count,
      time,
      progress,
      progressState,
      confirm,
      useVirtualBg,
      blur,
      showRetakeAndRemove,
      selectedBg,
    } = this.state

    const showVirtualBgToggle =
      IS_VIRTUAL_BG_SUPPORTED &&
      !isEmbedded &&
      state === 'preview' &&
      video &&
      !video.has &&
      this.streamsStarted

    const className = ['webcam', state, this.props.className].join(' ')
    return (
      <WebcamContainer>
        {confirm ? (
          <Confirm {...confirm} />
        ) : (
          <RecordingContainer className="recording-container">
            <StyledWebcam className={className} state={state}>
              <WebcamViewContainer className="webcam-view">
                <StyledVideo
                  state={state}
                  ref={this.video}
                  src={videoSource}
                  muted={muted}
                  hideInPreview={
                    useVirtualBg &&
                    this.video &&
                    this.video.current &&
                    this.video.current.videoWidth ===
                      HIGH_QUALITY_VIDEO_DIMENSIONS.width
                  }
                  width={this.videoWidth}
                  height={this.videoHeight}
                  onEnded={() => this.onEnded()}
                  controls={state === 'play'}
                />
                <canvas
                  style={{ display: 'none' }}
                  className="snapshot-canvas"
                  ref={this.snapshotCanvas}
                  width={width}
                  height={height}
                />
                <CenterizedContainer
                  className="webcam-centerize"
                  onClick={() => this.togglePlay()}
                  state={state}
                >
                  <StyledBgCanvas
                    width={this.videoWidth}
                    height={this.videoHeight}
                    blur={blur}
                    state={state}
                    ref={this.bgCanvas}
                  />
                  <OverlayActionsContainer>
                    <CloseButtonContainer
                      $state={state}
                      title={i18n.webcam.close}
                    >
                      <Button
                        variant="primary"
                        onClick={() => this.close()}
                        icon="close"
                        size="small"
                      />
                    </CloseButtonContainer>
                    {state === 'error' && (
                      <div className="webcam-error">
                        <Body pr="s4" pl="s4">
                          {errorMessage}
                        </Body>
                        {memoryError ? (
                          <PrimaryLink onClick={() => this.changeRecorder()}>
                            {i18n.webcam.error.alternativeRecorder}
                          </PrimaryLink>
                        ) : (
                          <Flex mt="s12" justifyContent="center">
                            <Button
                              variant="secondary"
                              icon="refresh"
                              onClick={() => this.setUpStreams()}
                            />
                          </Flex>
                        )}
                      </div>
                    )}
                    {(state === 'start' || state === 'loading') && (
                      <div className="webcam-start">
                        <Body>
                          {state === 'start'
                            ? i18n.webcam.starting
                            : i18n.webcam.processing}
                        </Body>
                        <LoadingIndicator />
                      </div>
                    )}
                    {state === 'countdown' && (
                      <StyledCountdown>{count}</StyledCountdown>
                    )}
                    {state === 'pause' && (
                      <Button
                        label="Play"
                        variant="tertiaryGhost"
                        icon="play_arrow"
                        // The whole container toggles play/pause so this button onclick fired it twice
                        onClick={() => {}}
                      />
                    )}
                  </OverlayActionsContainer>
                </CenterizedContainer>
                {state === 'record' && <WebcamTime>{time}</WebcamTime>}
                {progress ? (
                  <WebcamProgress
                    $converting={progressState === i18n.webcam.converting}
                    $progress={progress}
                    onTransitionEnd={() => this.onTransitionEnd()}
                  >
                    <ProgressText $progress={progress}>
                      {progressState}
                    </ProgressText>
                  </WebcamProgress>
                ) : null}
              </WebcamViewContainer>
              {!progress && (
                <RecorderButtons
                  onRecord={() => this.record()}
                  onStop={() => this.stop()}
                  button={this.selectButtons()}
                  onRemove={() => this.remove()}
                  showRetakeAndRemove={
                    isEmbedded ? showRetakeAndRemove || state === 'pause' : true
                  }
                  state={state}
                  onSubmit={state !== 'error' && onVideoSubmit}
                  disableRecord={disableRecording}
                  hasVideo={typeof video !== 'undefined' && video.has}
                  isEmbedded={isEmbedded}
                />
              )}
            </StyledWebcam>
            <>
              {useVirtualBg && (
                <VirtualBg
                  companyVideoBgImages={companyVideoBgImages}
                  setSelected={(background, blurAmount) =>
                    this.setSelectedBg(
                      background,
                      blurAmount,
                      companyVideoBgImages
                    )
                  }
                  selectedBg={selectedBg}
                  blur={blur}
                  showPhotos={showVirtualBgToggle}
                  initSelected={
                    (bgOptions &&
                      bgOptions.background &&
                      this.isInCompanyBgImages(bgOptions.background)) ||
                    isInBgImages(bgOptions.background)
                      ? bgOptions
                      : this.initialSelectedBg
                  }
                />
              )}
              {showVirtualBgToggle && (
                <Flex justifyContent="flex-end">
                  <Button
                    mt="s12"
                    id="toggle-bg-button"
                    variant="secondary"
                    onClick={() => this.onToggleBg()}
                    icon={useVirtualBg ? 'delete' : 'image'}
                    event={{
                      locationId: 'webcam',
                      id: useVirtualBg
                        ? 'removeVirtualBgButton'
                        : 'useVirtualBgButton',
                      properties: {
                        mode,
                      },
                    }}
                    text={
                      useVirtualBg
                        ? i18n.webcam.removeVideoBgButtonText
                        : i18n.webcam.useVideoBgButtonText
                    }
                  />
                </Flex>
              )}
            </>
          </RecordingContainer>
        )}
      </WebcamContainer>
    )
  }
}

WebcamHtml.propTypes = propTypes
WebcamHtml.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps())(WebcamHtml)
